/**
 * Notification permission helper.
 *
 * v1.1.1-b4: real iOS notification flow for the Broadcast Live Scan and the
 * scan-progress alerts (Wave 3 slide-over). Three goals:
 *
 *   1. Web is a no-op. Web builds never have ReplayKit or Apple's notification
 *      stack; the helper returns granted=true so callers do not branch.
 *
 *   2. On native, ask exactly once. The system itself returns "denied" forever
 *      if the user already said no AND we re-prompt; we mirror the granted /
 *      denied state into IndexedDB so the next mount of the broadcast pill
 *      skips the ask and goes straight to "Enable in Settings" copy.
 *
 *   3. If denied, expose a one-tap deep link into iOS Settings via the
 *      Capacitor App plugin (openSettings). The caller renders the copy and
 *      button; this module owns the open-settings call.
 *
 * Dynamic-import the Capacitor plugins so a dev build without the native
 * packages installed never crashes.
 */
import { getPlatform, isNative } from "./platform";
// Static imports, and the loaders below are SYNCHRONOUS on purpose
// (2026-09-10, two owner screenshots). A Capacitor plugin object is a Proxy
// that answers EVERY property name with a native method wrapper, `then`
// included, so a promise that resolves to one calls that fake `then`, whose
// callbacks are never invoked, and the await never returns. Returning a
// plugin from an async function, `await`ing one, or putting one through
// Promise.all all do exactly that. Every notification ask in the app went
// through such a loader, which is why iOS was never asked and the Settings
// check sat on "Plugin modules" until its timeout. Never let a plugin proxy
// cross a promise boundary: hand it over synchronously and only ever await
// its METHODS.
import { App as AppModule } from "@capacitor/app";
import { LocalNotifications as LocalNotificationsModule } from "@capacitor/local-notifications";
import { PushNotifications as PushNotificationsModule } from "@capacitor/push-notifications";

const IDB_NAME = "nexus_dex_prefs";
const IDB_STORE = "notification_permission";
const IDB_KEY = "v1";

export type PermissionState = "granted" | "denied" | "prompt" | "unknown";

/**
 * Fired on window when the OS gives a real answer to a permission ask
 * (detail: "granted" | "denied"), so the launch registration can run the
 * moment a trainer turns push on in Settings or on the broadcast pill
 * instead of waiting for the next launch (2026-10-07).
 */
export const NOTIFICATION_PERMISSION_EVENT = "nexus:notification-permission";

function announcePermission(state: PermissionState): void {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") return;
  try {
    window.dispatchEvent(new CustomEvent(NOTIFICATION_PERMISSION_EVENT, { detail: state }));
  } catch {
    /* an environment without CustomEvent: nothing listens there anyway */
  }
}

interface PersistedState {
  state: PermissionState;
  decidedAt: number;
}

// ── IndexedDB: tiny key-value wrapper ──────────────────────────────────────
//
// Using raw IDB avoids adding idb-keyval as a dependency for the single
// boolean we store. Two methods, both promise-based.

const IDB_OPEN_TIMEOUT_MS = 2_000;

function openDb(): Promise<IDBDatabase | null> {
  if (typeof window === "undefined" || !("indexedDB" in window)) {
    return Promise.resolve(null);
  }
  // An open that never calls back (WebKit has done this) must not hold the
  // permission ask hostage: the cache is a convenience, so it reads as
  // absent after two seconds and the flow goes on to ask the OS.
  return withTimeout(rawOpenDb(), IDB_OPEN_TIMEOUT_MS, "IndexedDB").catch(() => null);
}

function rawOpenDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = window.indexedDB.open(IDB_NAME, 1);
      req.onupgradeneeded = () => {
        try {
          req.result.createObjectStore(IDB_STORE);
        } catch {
          /* store already exists from a parallel open */
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function readCachedPermission(): Promise<PersistedState | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE, "readonly");
      const store = tx.objectStore(IDB_STORE);
      const req = store.get(IDB_KEY);
      req.onsuccess = () => {
        const v = req.result as PersistedState | undefined;
        resolve(v ?? null);
      };
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function writeCachedPermission(state: PermissionState): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const tx = db.transaction(IDB_STORE, "readwrite");
    tx.objectStore(IDB_STORE).put(
      { state, decidedAt: Date.now() } satisfies PersistedState,
      IDB_KEY,
    );
  } catch {
    /* best-effort persistence */
  }
}

// ── Plugin loaders ─────────────────────────────────────────────────────────
//
// Each returns the plugin proxy or null, never throws, and never awaits an
// import (see the note on the static imports above). The bridge's own
// registry is the fallback: on a native build it holds every plugin the
// shell registered, so even a module that failed to evaluate cannot leave
// the caller without a way to talk to the native side.

interface BridgeShape {
  Plugins?: Record<string, unknown>;
  isPluginAvailable?: (name: string) => boolean;
  registerPlugin?: (name: string) => unknown;
}

function bridgePlugin(name: string): any | null {
  if (typeof window === "undefined") return null;
  const cap = (window as unknown as { Capacitor?: BridgeShape }).Capacitor;
  const listed = cap?.Plugins?.[name];
  if (listed) return listed;
  if (typeof cap?.registerPlugin === "function" && cap.isPluginAvailable?.(name)) {
    try {
      return cap.registerPlugin(name) ?? null;
    } catch {
      return null;
    }
  }
  return null;
}

function loadPushPlugin(): any | null {
  return PushNotificationsModule ?? bridgePlugin("PushNotifications");
}

function loadLocalPlugin(): any | null {
  return LocalNotificationsModule ?? bridgePlugin("LocalNotifications");
}

function loadAppPlugin(): any | null {
  return AppModule ?? bridgePlugin("App");
}

/**
 * Android remote push (2026-09-30). The native PushSupport plugin says
 * whether this build carries a Firebase configuration (google-services.json
 * at build time). Without one the push plugin's register() throws on its
 * native thread and the process dies (the 1.9.0 Play launch crash, PR #945),
 * so registration is gated on this answer, cached per session. iOS is
 * always available (APNs); web never.
 */
let remotePushAvailable: boolean | null = null;

export function isRemotePushAvailable(): boolean {
  if (!isNative()) return false;
  if (getPlatform() === "ios") return true;
  return remotePushAvailable === true;
}

export async function probeRemotePushAvailability(): Promise<boolean> {
  if (!isNative()) return false;
  if (getPlatform() === "ios") return true;
  if (remotePushAvailable !== null) return remotePushAvailable;
  const Support = bridgePlugin("PushSupport");
  if (!Support || typeof Support.isConfigured !== "function") {
    remotePushAvailable = false;
    return false;
  }
  try {
    const res: any = await withTimeout(Support.isConfigured(), NATIVE_CALL_TIMEOUT_MS, "Push support");
    remotePushAvailable = res?.configured === true;
  } catch {
    remotePushAvailable = false;
  }
  return remotePushAvailable;
}

/** Test seam. */
export function __resetRemotePushAvailabilityForTests(): void {
  remotePushAvailable = null;
}

/** The Android channel the worker names in every FCM payload (lib/push.ts). */
export const ANDROID_PUSH_CHANNEL = {
  id: "nexus_push",
  name: "Nexus Dex",
  description: "Friend requests, trades and dex updates from Nexus Dex",
  importance: 4,
  visibility: 1,
} as const;

/**
 * A native call that never answers must not hang a flow for ever (2026-09-10
 * owner report: the test button gave no response at all, no toast, nothing).
 * Every plugin call below races this; a timeout reads as an error with a
 * message, never as a trainer who said no.
 */
export const NATIVE_CALL_TIMEOUT_MS = 6_000;
/**
 * A permission ask waits on the trainer, not on the OS: the system dialog
 * stays up as long as they take (2026-09-30 Galaxy: an answer after six
 * seconds read as "unknown", a second ask fired over the first dialog, and
 * the boot ask was marked done anyway).
 */
export const PERMISSION_ASK_TIMEOUT_MS = 90_000;

// ── Android channel (2026-09-30) ─────────────────────────────────────────────
// The plugin's own "default" channel is IMPORTANCE_DEFAULT: a status-bar icon
// and a shade entry, no banner, which on a Galaxy reads as "nothing came".
// Every reminder goes through this high-importance channel instead. iOS has
// no channels; the field is ignored there.
export const ANDROID_REMINDER_CHANNEL = {
  id: "event_reminders",
  name: "Event reminders",
  description: "Event starts and other reminders you set in Nexus Dex",
  importance: 4,
  visibility: 1,
} as const;
let androidChannelReady = false;

async function ensureAndroidChannel(Local: any): Promise<void> {
  if (getPlatform() !== "android" || androidChannelReady) return;
  if (typeof Local?.createChannel !== "function") return;
  try {
    await withTimeout(Local.createChannel(ANDROID_REMINDER_CHANNEL), NATIVE_CALL_TIMEOUT_MS, "Create channel");
    androidChannelReady = true;
  } catch {
    // Creating a channel that exists is a no-op natively; a failure here
    // falls back to the plugin's default channel on this schedule.
  }
}

/**
 * Android-only fields on a scheduled notification. `isExactNotification`
 * defaults to TRUE in the plugin, and on Android 14+ a fresh install has no
 * exact-alarm grant, so every schedule() first bounced the trainer to the
 * "Alarms & reminders" settings screen and the 6 s fence then reported
 * "Could not set the notification" (2026-09-30 Galaxy). An inexact alarm with
 * allowWhileIdle lands within minutes of the event start, which is what a
 * reminder needs, with no settings detour.
 */
function androidNotificationFields(): { channelId?: string; isExactNotification?: boolean } {
  if (getPlatform() !== "android") return {};
  return { channelId: ANDROID_REMINDER_CHANNEL.id, isExactNotification: false };
}

/** Test seam. */
export function __resetAndroidChannelForTests(): void {
  androidChannelReady = false;
}

export function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`${label} did not answer within ${Math.round(ms / 1000)}s`)),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

// ── Public API ─────────────────────────────────────────────────────────────

/**
 * Read the cached permission state without prompting. Useful for the
 * broadcast pill to decide whether to render "permission denied" copy on
 * first paint, before we even consider the OS prompt.
 */
export async function getCachedNotificationPermission(): Promise<PermissionState> {
  if (!isNative()) return "granted";
  const cached = await readCachedPermission();
  return cached?.state ?? "unknown";
}

/**
 * Request notification permission once. Idempotent across mounts thanks to
 * the IDB cache: if the user already granted or denied, we return the
 * cached value without re-prompting (iOS itself would silently return the
 * same answer the second time, but the cache lets the UI render the right
 * affordance on first paint).
 *
 * Web returns "granted" so callers never branch on platform.
 */
/**
 * The OS's CURRENT answer, without prompting (2026-09-10). The cache below
 * is a first-paint convenience, not the truth: a trainer who turned
 * notifications on in iOS Settings after an early "deny" would otherwise
 * be told "denied" by this app forever. Web reads as granted; a plugin that
 * cannot be loaded reads as unknown, which is not a refusal.
 */
export async function checkNotificationPermission(): Promise<PermissionState> {
  if (!isNative()) return "granted";
  const Local = loadLocalPlugin();
  if (!Local?.checkPermissions) return "unknown";
  try {
    const res: any = await withTimeout(Local.checkPermissions(), NATIVE_CALL_TIMEOUT_MS, "Permission check");
    return mapDisplayState(res?.display);
  } catch {
    return "unknown";
  }
}

export async function requestNotificationPermission(opts?: {
  reprompt?: boolean;
}): Promise<PermissionState> {
  if (!isNative()) return "granted";

  if (!opts?.reprompt) {
    const cached = await readCachedPermission();
    if (cached?.state === "granted") return cached.state;
    // A cached "denied" is re-checked against the OS before it is believed
    // (2026-09-10): the trainer may have turned notifications on in Settings
    // since, and a cache that could only ever say no had no way back. The
    // check never prompts, so a real refusal costs nothing extra.
    if (cached?.state === "denied") {
      const live = await checkNotificationPermission();
      if (live === "granted") {
        await writeCachedPermission("granted");
        return "granted";
      }
      if (live === "denied") return "denied";
      // unknown / prompt: fall through and ask properly.
    }
  }

  // Two parallel asks: push-notifications (banner) and local-notifications
  // (in-app scan-progress alerts). iOS surfaces one combined dialog so the
  // user sees one tap.
  const Push = loadPushPlugin();
  const Local = loadLocalPlugin();

  let pushState: PermissionState = "unknown";
  let localState: PermissionState = "unknown";

  // A plugin that THROWS is not a trainer who said no. It used to be recorded
  // as "denied" and then cached, so one native hiccup on first launch switched
  // every notification off for good (2026-09-10 owner report: never received
  // a single one). Unknown is left uncached and asked again next time.
  // Android: ONE ask (2026-09-30). Both plugins request the same
  // POST_NOTIFICATIONS there, so the push ask is a duplicate dialog, and
  // remote push has no Android delivery path anyway (no Firebase project;
  // the worker sends to APNs only). Local notifications carry every
  // reminder on Android.
  if (Push && getPlatform() !== "android") {
    try {
      const res: any = await withTimeout(Push.requestPermissions(), PERMISSION_ASK_TIMEOUT_MS, "Push permission");
      pushState = mapReceiveState(res?.receive);
    } catch {
      pushState = "unknown";
    }
  }
  if (Local) {
    try {
      const res: any = await withTimeout(Local.requestPermissions(), PERMISSION_ASK_TIMEOUT_MS, "Local permission");
      localState = mapDisplayState(res?.display);
    } catch {
      localState = "unknown";
    }
  }

  // Coalesce: if either path got "granted" we consider permission granted.
  // Most trainers will see one dialog; coalescing avoids needing both green
  // for the broadcast pipeline to work.
  const final: PermissionState =
    pushState === "granted" || localState === "granted"
      ? "granted"
      : pushState === "denied" || localState === "denied"
        ? "denied"
        : pushState === "prompt" || localState === "prompt"
          ? "prompt"
          : "unknown";

  // Only a real answer is worth remembering.
  if (final === "granted" || final === "denied") {
    await writeCachedPermission(final);
    announcePermission(final);
  }
  return final;
}

function mapReceiveState(s: unknown): PermissionState {
  if (s === "granted") return "granted";
  if (s === "denied") return "denied";
  if (s === "prompt" || s === "prompt-with-rationale") return "prompt";
  return "unknown";
}
function mapDisplayState(s: unknown): PermissionState {
  if (s === "granted") return "granted";
  if (s === "denied") return "denied";
  if (s === "prompt") return "prompt";
  return "unknown";
}


/**
 * Workstream D — APNs push registration.
 *
 * Registers for remote notifications, captures the APNs device token via the
 * Capacitor push-notifications plugin's `registration` event, and POSTs the
 * hex token to `/api/notifications/register`. Idempotent: the server upserts
 * on (account_id, apns_token).
 *
 * The Capacitor plugin returns the token as a base64 / hex string depending
 * on platform version. Apple's wire format is hex; we normalise to lowercase
 * hex before sending. If we receive something that isn't hex we bail out
 * silently rather than corrupting the device row.
 *
 * Web returns null. iOS without permission returns null. Failures are
 * swallowed; this never blocks the value moment that triggered it.
 */
export async function registerForPushNotifications(
  apiBase: string,
  bearerToken: string | null,
): Promise<string | null> {
  if (!isNative()) return null;
  if (!bearerToken) return null;
  // Android registers only when the build carries a Firebase configuration
  // (2026-09-30, PushSupport.isConfigured). Without one the plugin's
  // register() throws IllegalStateException on its native thread and the
  // process dies; a JS try/catch never sees it. That was the 1.9.0 Play
  // report "crashes on start (restoring the session) and again after Google
  // login" (2026-09-16, reproduced on the owner's S21+).
  const platform = getPlatform();
  if (platform !== "ios" && platform !== "android") return null;
  if (platform === "android" && !(await probeRemotePushAvailability())) return null;
  const Push = loadPushPlugin();
  if (!Push) return null;

  // The plugin emits 'registration' or 'registrationError' once after
  // register() is called. Wrap in a one-shot promise with a generous timeout
  // — APNs network calls can be slow on cold launch.
  const tokenPromise: Promise<string | null> = new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), 8000);
    let resolved = false;
    const settle = (value: string | null) => {
      if (resolved) return;
      resolved = true;
      clearTimeout(timer);
      resolve(value);
    };
    try {
      Push.addListener("registration", (token: { value?: string } | string) => {
        const raw = typeof token === "string" ? token : token?.value;
        settle(typeof raw === "string" ? raw : null);
      });
      Push.addListener("registrationError", () => settle(null));
    } catch {
      settle(null);
    }
  });

  try {
    await Push.register();
  } catch {
    return null;
  }

  const raw = await tokenPromise;
  if (!raw) return null;
  let body: Record<string, string>;
  let token: string;
  if (platform === "android") {
    // An FCM registration token: opaque, case-sensitive, sent as-is.
    token = raw.trim();
    if (!/^[A-Za-z0-9:_-]{20,4096}$/.test(token)) return null;
    body = { fcmToken: token, platform: "android" };
    // The channel the worker names in every payload; creating an existing
    // channel is a no-op natively. Best-effort.
    try {
      if (typeof Push.createChannel === "function") {
        await withTimeout(Push.createChannel(ANDROID_PUSH_CHANNEL), NATIVE_CALL_TIMEOUT_MS, "Push channel");
      }
    } catch {
      /* the plugin's default channel still shows the push */
    }
  } else {
    const hexToken = normaliseApnsToken(raw);
    if (!hexToken) return null;
    token = hexToken;
    body = { apnsToken: hexToken };
  }

  try {
    const res = await fetch(`${apiBase}/api/notifications/register`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: JSON.stringify(body),
    });
    if (!res.ok) return null;
  } catch {
    return null;
  }
  return token;
}

/**
 * Apple's device token wire format is 32 bytes of hex. The Capacitor plugin
 * may hand it back as base64 (older builds) or plain hex (current). We
 * accept both, return lowercase hex, or null if the input isn't recognisable.
 */
function normaliseApnsToken(raw: string): string | null {
  const trimmed = raw.trim();
  if (/^[0-9a-fA-F]+$/.test(trimmed) && trimmed.length >= 32 && trimmed.length <= 400) {
    return trimmed.toLowerCase();
  }
  // Try base64 -> hex.
  try {
    const bin = typeof atob === "function" ? atob(trimmed) : "";
    if (!bin) return null;
    let hex = "";
    for (let i = 0; i < bin.length; i += 1) {
      hex += bin.charCodeAt(i).toString(16).padStart(2, "0");
    }
    if (hex.length >= 32 && hex.length <= 400) return hex;
  } catch {
    return null;
  }
  return null;
}

/**
 * Register the APNs token on app launch for an already-permitted native user.
 *
 * Unlike requestNotificationPermission this NEVER prompts: it only re-runs the
 * token capture + POST when permission is ALREADY granted. We previously only
 * registered inside a one-time Settings gesture / value-moment prompt, so a
 * device whose token rotated (Apple rotates on restore/reinstall) or that
 * granted permission on another install could end up with no token on file —
 * and every sendPush for that account then dies `no_devices`. Calling this once
 * per launch keeps a fresh token registered. No-op on web, when permission is
 * not granted, or without a bearer token.
 */
export async function registerPushOnLaunch(
  apiBase: string,
  bearerToken: string | null,
): Promise<void> {
  if (!isNative() || !bearerToken) return;
  // Android registers only on a Firebase-configured build (the probe inside
  // registerForPushNotifications); a build without one returns early there.
  //
  // THE OS IS ASKED, NOT THE CACHE (2026-10-07). This used to read only the
  // app's own IndexedDB note of the last answer, which is empty on a fresh
  // install, a new phone or a restore, and stays empty for anyone who turned
  // notifications on in iOS Settings rather than through one of the app's
  // prompts. Permission was granted at the OS level, no token was ever put
  // on file, and every push for that account died "no_devices". The cache
  // is only the fallback for a plugin that cannot answer.
  const live = await checkNotificationPermission();
  let granted = live === "granted";
  if (live === "unknown") granted = (await getCachedNotificationPermission()) === "granted";
  if (!granted) return;
  if (live === "granted") void writeCachedPermission("granted");
  try {
    await registerForPushNotifications(apiBase, bearerToken);
  } catch {
    /* best-effort; the next launch retries */
  }
}

/**
 * Wire the foreground-display + tap listeners for REMOTE pushes. Returns a
 * cleanup that removes them.
 *
 *   - onForeground fires when a push lands while the app is open. iOS renders
 *     the banner itself via presentationOptions (capacitor.config.ts); this
 *     hook is for side effects like refreshing the in-app inbox badge.
 *   - onTap fires when the user taps a delivered push (foreground or
 *     background), so we can route to the notification's target.
 *
 * No-op on web / when the plugin is missing.
 */
export async function addPushDisplayListeners(handlers: {
  onForeground?: (n: { title?: string; body?: string; data: Record<string, any> }) => void;
  onTap?: (data: Record<string, any>) => void;
}): Promise<() => void> {
  if (!isNative()) return () => {};
  const Push = loadPushPlugin();
  if (!Push) return () => {};
  const subs: Array<{ remove?: () => void }> = [];
  try {
    subs.push(
      await Push.addListener("pushNotificationReceived", (n: any) => {
        handlers.onForeground?.({ title: n?.title, body: n?.body, data: n?.data ?? {} });
      }),
    );
    subs.push(
      await Push.addListener("pushNotificationActionPerformed", (a: any) => {
        handlers.onTap?.(a?.notification?.data ?? {});
      }),
    );
  } catch {
    /* listeners are best-effort; never throw into launch */
  }
  return () => {
    for (const s of subs) {
      try {
        s?.remove?.();
      } catch {
        /* ignore */
      }
    }
  };
}

/**
 * Schedule a one-off local notification. Used by the broadcast hook to fire
 * "Nexus Dex is watching your scan" and "Scan complete" at the lifecycle
 * boundaries. No-op on web and when the plugin is missing.
 */
/**
 * Schedule a local notification for a FUTURE device-local instant (the
 * transfer-bonus "remind me": the Date comes from eventCountdownDate, the
 * same wall-clock recovery the calendar invites use, so the alert lands at
 * the event's start in the trainer's own zone). Web is a no-op — the in-app
 * banner covers it there. A past instant schedules nothing.
 */
export type ScheduleReason = "scheduled" | "web" | "invalid" | "past" | "no-plugin" | "error";

export interface ScheduleResult {
  ok: boolean;
  /** Why nothing was scheduled, so a surface can say so instead of "Reminder set." */
  reason: ScheduleReason;
  /** The native error's message on "error", for the diagnostics toast. */
  detail?: string;
}

export async function scheduleLocalNotificationAt(opts: {
  id: number;
  title: string;
  body: string;
  at: Date;
}): Promise<ScheduleResult> {
  if (!isNative()) return { ok: false, reason: "web" };
  if (!(opts.at instanceof Date) || Number.isNaN(opts.at.getTime())) return { ok: false, reason: "invalid" };
  if (opts.at.getTime() <= Date.now()) return { ok: false, reason: "past" };
  const Local = loadLocalPlugin();
  if (!Local?.schedule) return { ok: false, reason: "no-plugin" };
  try {
    await ensureAndroidChannel(Local);
    await withTimeout(
      Local.schedule({
        notifications: [
          {
            id: opts.id,
            title: opts.title,
            body: opts.body,
            // allowWhileIdle: Android Doze would otherwise hold an exact alarm
            // past the event's start; iOS ignores the flag.
            schedule: { at: opts.at, allowWhileIdle: true },
            ...androidNotificationFields(),
          },
        ],
      }),
      NATIVE_CALL_TIMEOUT_MS,
      "Schedule",
    );
    return { ok: true, reason: "scheduled" };
  } catch (err) {
    return { ok: false, reason: "error", detail: (err as Error)?.message };
  }
}

/** Plain words for a schedule outcome, for a toast. */
export function describeScheduleResult(r: ScheduleResult): string {
  switch (r.reason) {
    case "scheduled":
      return "Scheduled.";
    case "web":
      return "Notifications only fire from the installed app, not the browser.";
    case "invalid":
      return "The event has no usable start time.";
    case "past":
      return "That time has already passed.";
    case "no-plugin":
      return "The notifications plugin is missing from this build.";
    case "error":
      return `The device refused it${r.detail ? `: ${r.detail}` : ""}.`;
  }
}

/**
 * The diagnostics button in Settings (2026-09-10 owner report: never received
 * a notification). Asks for permission the proper way, then schedules one a
 * few seconds out, and reports both halves so the trainer can see exactly
 * which step is failing on their device.
 */
export async function sendTestNotification(): Promise<{
  permission: PermissionState;
  schedule: ScheduleResult;
}> {
  const permission = await requestNotificationPermission({ reprompt: true });
  if (permission !== "granted") {
    return { permission, schedule: { ok: false, reason: "error", detail: `permission ${permission}` } };
  }
  const schedule = await scheduleLocalNotificationAt({
    id: 900001,
    title: "Nexus Dex test",
    body: "Notifications reach this device. Event reminders will land the same way.",
    at: new Date(Date.now() + 5_000),
  });
  return { permission, schedule };
}

// ── On-device diagnostics ──────────────────────────────────────────────────

export interface DiagStep {
  /** Short label for the row. */
  name: string;
  /** true = green, false = red, null = skipped (grey). */
  ok: boolean | null;
  /** What came back, in plain words. */
  detail: string;
  /** How long the step took. */
  ms: number;
}

export interface NotificationDiagnostics {
  steps: DiagStep[];
  /** Every step on one line each, for copying into a bug report. */
  text: string;
  /** The test notification is queued and permission is granted. */
  ok: boolean;
}

interface CapacitorProbe {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  isPluginAvailable?: (name: string) => boolean;
  /** The native bridge's registered plugins, injected at load on iOS and Android. */
  PluginHeaders?: unknown[];
}

function probeCapacitor(): CapacitorProbe | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as unknown as { Capacitor?: CapacitorProbe }).Capacitor;
}

function describeError(e: unknown): string {
  if (e instanceof Error) return e.message;
  if (typeof e === "string") return e;
  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

/**
 * Walk the whole path a reminder takes, one native call at a time, and say
 * what each one answered (2026-09-10 owner report: the test button gave no
 * response and Nexus Dex is absent from iOS Settings > Notifications, which
 * means the OS has never been asked). Each call is raced against
 * NATIVE_CALL_TIMEOUT_MS so a call that never answers is a red row with a
 * message, not silence. Nothing here throws.
 */
export async function runNotificationDiagnostics(
  onStep?: (steps: DiagStep[]) => void,
): Promise<NotificationDiagnostics> {
  const steps: DiagStep[] = [];
  const t0 = Date.now();
  let last = t0;
  const push = (name: string, ok: boolean | null, detail: string) => {
    const now = Date.now();
    steps.push({ name, ok, detail, ms: now - last });
    last = now;
    onStep?.([...steps]);
  };
  const run = async <T,>(name: string, fn: () => Promise<T>, say: (v: T) => [boolean, string]): Promise<T | undefined> => {
    try {
      const v = await withTimeout(fn(), NATIVE_CALL_TIMEOUT_MS, name);
      const [ok, detail] = say(v);
      push(name, ok, detail);
      return v;
    } catch (e) {
      push(name, false, describeError(e));
      return undefined;
    }
  };

  // 1. Are we even inside the native shell, and does it know the plugins?
  const cap = probeCapacitor();
  const platform = getPlatform();
  const localAvail = cap?.isPluginAvailable?.("LocalNotifications");
  const pushAvail = cap?.isPluginAvailable?.("PushNotifications");
  const headerCount = Array.isArray(cap?.PluginHeaders) ? cap.PluginHeaders.length : undefined;
  push(
    "Native shell",
    platform !== "web",
    platform === "web"
      ? "Running as a web page: the bridge is missing, so nothing native can fire."
      : `${platform}, ${headerCount === undefined ? "no plugin list" : `${headerCount} native plugins`}, LocalNotifications ${localAvail === undefined ? "unchecked" : localAvail ? "registered" : "NOT registered"}, PushNotifications ${pushAvail === undefined ? "unchecked" : pushAvail ? "registered" : "NOT registered"}`,
  );
  if (platform === "web") {
    return finish(steps, false);
  }

  // 2. The plugin proxies themselves (static modules, bridge registry as
  //    the fallback). Timed so a stall here can never again be silence.
  // Synchronous by design: a plugin proxy must never be awaited (see the
  // note on the imports at the top of this file).
  let Local: any = null;
  let Push: any = null;
  try {
    Local = loadLocalPlugin();
    Push = loadPushPlugin();
    push(
      "Plugin modules",
      !!Local,
      `local ${Local ? "loaded" : "MISSING"}, push ${Push ? "loaded" : "MISSING"}, via ${LocalNotificationsModule ? "static import" : "bridge registry"}`,
    );
  } catch (e) {
    push("Plugin modules", false, describeError(e));
  }
  if (!Local) return finish(steps, false);

  // 3. What the OS says right now, without prompting.
  await run(
    "Permission now",
    () => Local.checkPermissions() as Promise<{ display?: string }>,
    (v) => [v?.display === "granted", `display = ${String(v?.display)}`],
  );

  // 4. Ask, both plugins, the way the app does.
  if (Push) {
    await run(
      "Ask (push)",
      () => Push.requestPermissions() as Promise<{ receive?: string }>,
      (v) => [v?.receive === "granted", `receive = ${String(v?.receive)}`],
    );
  } else {
    push("Ask (push)", null, "skipped, module missing");
  }
  const asked = await run(
    "Ask (local)",
    () => Local.requestPermissions() as Promise<{ display?: string }>,
    (v) => [v?.display === "granted", `display = ${String(v?.display)}`],
  );
  const granted = asked?.display === "granted";
  if (asked?.display === "granted" || asked?.display === "denied") {
    await writeCachedPermission(asked.display);
  }

  // 5. Queue one five seconds out, then prove it is in the queue.
  const at = new Date(Date.now() + 5_000);
  const scheduled = await run(
    "Schedule test",
    () =>
      Local.schedule({
        notifications: [
          {
            id: 900001,
            title: "Nexus Dex test",
            body: "Notifications reach this device. Event reminders will land the same way.",
            schedule: { at, allowWhileIdle: true },
            ...androidNotificationFields(),
          },
        ],
      }) as Promise<unknown>,
    () => [true, `id 900001 for ${at.toLocaleTimeString()}`],
  );
  const pending = await run(
    "Pending queue",
    () => Local.getPending() as Promise<{ notifications?: Array<{ id?: number }> }>,
    (v) => {
      const list = v?.notifications ?? [];
      const mine = list.some((n) => Number(n?.id) === 900001);
      return [mine, `${list.length} pending, test ${mine ? "queued" : "NOT in the queue"}`];
    },
  );
  const queued = (pending?.notifications ?? []).some((n) => Number(n?.id) === 900001);

  return finish(steps, granted && scheduled !== undefined && queued, t0);
}

function finish(steps: DiagStep[], ok: boolean, t0 = Date.now()): NotificationDiagnostics {
  const platform = getPlatform();
  const lines = [
    `Nexus Dex notification check, ${new Date().toISOString()}, ${platform}, ${Date.now() - t0}ms`,
    ...steps.map((s) => `${s.ok === null ? "-" : s.ok ? "OK " : "FAIL"} ${s.name}: ${s.detail} (${s.ms}ms)`),
  ];
  return { steps, text: lines.join("\n"), ok };
}

/**
 * Fire one now. Says whether it did (2026-09-30): "Send me an example" used
 * to swallow every rejection (NOTIFICATIONS_DISABLED included) behind a toast
 * that looked like success, so an Android trainer had no way to learn the
 * OS had said no.
 */
export async function fireLocalNotification(opts: {
  id: number;
  title: string;
  body: string;
}): Promise<{ ok: boolean; detail?: string }> {
  if (!isNative()) return { ok: false, detail: "web" };
  const Local = loadLocalPlugin();
  if (!Local?.schedule) return { ok: false, detail: "no-plugin" };
  try {
    await ensureAndroidChannel(Local);
    await withTimeout(
      Local.schedule({
        notifications: [
          {
            id: opts.id,
            title: opts.title,
            body: opts.body,
            // Fire immediately. iOS still rate-limits if we fire many at once.
            schedule: { at: new Date(Date.now() + 100) },
            ...androidNotificationFields(),
          },
        ],
      }),
      NATIVE_CALL_TIMEOUT_MS,
      "Example",
    );
    return { ok: true };
  } catch (err) {
    return { ok: false, detail: (err as Error)?.message ?? String(err) };
  }
}
